/**
 * `v-overflow-title`: the full text of an ellipsis-truncated element in a
 * tooltip, only while it is actually cut off.
 *
 * Usage: register it locally in the component that uses it
 * (`import overflowTitle from "@/directives/overflowTitle"` and
 * `directives: { overflowTitle }`), then put it on the element that carries
 * the overflow style (`text-truncate`, a nowrap/hidden/ellipsis class, or a
 * `-webkit-line-clamp` block). With no value the tooltip shows the element's
 * own text; pass a value when the rendered text is not the full value (a chip
 * label inside the cell, a child component). Pass `false` to switch it off
 * where the element already shows its value on hover some other way. An
 * element with its own `title` keeps it: the directive shows nothing there.
 *
 * The tooltip appears once the pointer has rested on the element for
 * OVERFLOW_TITLE_DELAY_MS and goes away when it leaves or anything scrolls,
 * so cells sliding under a stationary pointer never flash one.
 *
 * Why one shared element and not a native `title`: Chrome decides whether to
 * show a native title when the pointer stops, so a title added after the
 * rest delay did not appear until the reviewer nudged the mouse. Why not
 * `v-tooltip`: the QC grids (Data Preview, Merge) render thousands of cells,
 * so the directive keeps no reactive state and an idle cell costs one
 * mouseenter listener. The move and leave listeners exist only while the
 * pointer is inside the element, the window scroll listener only while a
 * wait is pending or the tooltip is up, and the one layout read happens when
 * the timer fires. The element is styled from Vuetify's own tooltip theme
 * variables, so it looks like every other QC tooltip.
 */

export const OVERFLOW_TITLE_DELAY_MS = 400;

/** Gap between the element and the tooltip, and the viewport margin. */
const OFFSET_PX = 6;
const MARGIN_PX = 8;

const TOOLTIP_ID = "qc-overflow-tooltip";

// Per-element state lives on the element under a private key, so the
// directive needs no map and nothing outlives the element.
const STATE = "__overflowTitle";

// Elements with a timer running. The window scroll listener is attached while
// this is non-empty or the tooltip is showing, and detached otherwise.
const pending = new Set();
const SCROLL_EVENTS = ["scroll", "wheel"];
const SCROLL_OPTIONS = { capture: true, passive: true };

let tooltip = null;
let shownFor = null;
let listening = false;

/** The one tooltip element, created on first use. */
export function overflowTooltipElement() {
	if (tooltip && tooltip.isConnected) return tooltip;
	tooltip = document.createElement("div");
	tooltip.id = TOOLTIP_ID;
	tooltip.setAttribute("role", "tooltip");
	// The element sits on <body>, outside the app root that sets the font.
	const appRoot = document.querySelector(".v-application") || document.body;
	Object.assign(tooltip.style, {
		fontFamily: window.getComputedStyle(appRoot).fontFamily,
		position: "fixed",
		zIndex: "3000",
		display: "none",
		pointerEvents: "none",
		maxWidth: "min(480px, calc(100vw - 16px))",
		padding: "5px 16px",
		borderRadius: "4px",
		fontSize: "0.875rem",
		lineHeight: "1.6",
		whiteSpace: "normal",
		overflowWrap: "anywhere",
		background: "rgba(var(--v-theme-surface-variant), 0.9)",
		color: "rgb(var(--v-theme-on-surface-variant))",
	});
	document.body.appendChild(tooltip);
	return tooltip;
}

function isClamped(el) {
	const style = window.getComputedStyle(el);
	const clamp =
		style.webkitLineClamp || style.getPropertyValue("-webkit-line-clamp");
	return !!clamp && clamp !== "none";
}

export function isTruncated(el) {
	if (el.scrollWidth > el.clientWidth) return true;
	// Height only counts for a line-clamp block: a single-line element's
	// descenders can exceed its line box by a pixel, which is not truncation.
	return el.scrollHeight > el.clientHeight && isClamped(el);
}

function fullText(el, state) {
	if (state.value !== undefined && state.value !== null) {
		return String(state.value).trim();
	}
	return (el.textContent || "").trim();
}

function syncScrollListener() {
	const want = pending.size > 0 || shownFor !== null;
	if (want === listening) return;
	for (const type of SCROLL_EVENTS) {
		if (want) window.addEventListener(type, onScroll, SCROLL_OPTIONS);
		else window.removeEventListener(type, onScroll, SCROLL_OPTIONS);
	}
	listening = want;
}

/** Below the element, flipped above when it would leave the viewport. */
function place(el, tip) {
	const rect = el.getBoundingClientRect();
	const width = tip.offsetWidth;
	const height = tip.offsetHeight;
	const maxLeft = window.innerWidth - width - MARGIN_PX;
	const left = Math.max(MARGIN_PX, Math.min(rect.left, maxLeft));
	let top = rect.bottom + OFFSET_PX;
	if (top + height > window.innerHeight - MARGIN_PX) {
		top = Math.max(MARGIN_PX, rect.top - OFFSET_PX - height);
	}
	tip.style.left = `${left}px`;
	tip.style.top = `${top}px`;
}

function show(el, text) {
	const tip = overflowTooltipElement();
	tip.textContent = text;
	tip.style.visibility = "hidden";
	tip.style.display = "block";
	place(el, tip);
	tip.style.visibility = "visible";
	el.setAttribute("aria-describedby", TOOLTIP_ID);
	shownFor = el;
	syncScrollListener();
}

function hide(el) {
	if (shownFor !== el) return;
	if (tooltip) tooltip.style.display = "none";
	el.removeAttribute("aria-describedby");
	shownFor = null;
	syncScrollListener();
}

/** Show the tooltip for `el` if its text is cut off now, else hide it. */
function refresh(el) {
	const state = el[STATE];
	if (!state) return;
	// A title the template set is the element's own hover text; leave it.
	if (el.hasAttribute("title")) {
		hide(el);
		return;
	}
	const text = state.value === false ? "" : fullText(el, state);
	if (text && isTruncated(el)) show(el, text);
	else hide(el);
}

function onScroll() {
	for (const el of [...pending]) cancel(el);
	if (shownFor) hide(shownFor);
}

function cancel(el) {
	const state = el[STATE];
	if (state && state.timer !== null) {
		clearTimeout(state.timer);
		state.timer = null;
	}
	pending.delete(el);
	syncScrollListener();
}

function schedule(el) {
	const state = el[STATE];
	if (!state) return;
	if (state.timer !== null) clearTimeout(state.timer);
	pending.add(el);
	syncScrollListener();
	state.timer = setTimeout(() => {
		state.timer = null;
		// Show before dropping the wait, so the scroll listener is handed
		// from the wait to the tooltip instead of removed and re-added.
		refresh(el);
		cancel(el);
	}, OVERFLOW_TITLE_DELAY_MS);
}

function onMouseMove(event) {
	const el = event.currentTarget;
	// Moving inside the element restarts the wait, so the tooltip only comes
	// up once the pointer rests (and comes back after a scroll hid it). Once
	// it is showing for this element, moving is free.
	if (shownFor !== el) schedule(el);
}

function onMouseLeave(event) {
	const el = event.currentTarget;
	el.removeEventListener("mousemove", onMouseMove);
	el.removeEventListener("mouseleave", onMouseLeave);
	cancel(el);
	hide(el);
}

function onMouseEnter(event) {
	const el = event.currentTarget;
	if (!el[STATE]) return;
	el.addEventListener("mousemove", onMouseMove);
	el.addEventListener("mouseleave", onMouseLeave);
	schedule(el);
}

export default {
	mounted(el, binding) {
		el[STATE] = { value: binding.value, timer: null };
		el.addEventListener("mouseenter", onMouseEnter);
	},
	updated(el, binding) {
		const state = el[STATE];
		if (!state) return;
		state.value = binding.value;
		// Only the element the tooltip is showing for needs a re-check: its
		// text or width may have changed under the pointer.
		if (shownFor === el) refresh(el);
	},
	beforeUnmount(el) {
		cancel(el);
		hide(el);
		el.removeEventListener("mouseenter", onMouseEnter);
		el.removeEventListener("mousemove", onMouseMove);
		el.removeEventListener("mouseleave", onMouseLeave);
		delete el[STATE];
	},
};
